import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Video, Megaphone, Users, UserCog, ScreenShare, Mic, Calendar, FileVideo, Sparkles } from 'lucide-react';
import * as Icons from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import FaqAccordion from '../components/FaqAccordion';
import Seo from '../components/Seo';
import { VIDEO_FEATURES, NEW_PAGE_IMAGES } from '../data/mock';

const FAQ = [
  { q: 'How many people can join a meeting?', a: 'A conference / collaboration session supports up to 25 hosts, attendees or guests in full video mode, plus another 25 audio-only participants — 50 total. Webinar sessions scale to 1,000 hosts, attendees and guests.' },
  { q: "What's the difference between a meeting, a conference room and a webinar?", a: "A meeting is a one-off scheduled session with a unique link. A conference room is a persistent room with its own URL you can reuse. A webinar is built for 1,000 viewers with host / attendee / guest roles." },
  { q: 'Is video conferencing included with Business VoIP?', a: 'Yes — every Business and Enterprise seat includes unlimited HD video meetings at no extra charge. No separate Zoom / Teams licence required.' },
  { q: 'Can external guests join without an account?', a: 'Yes. Guests join via a browser link — no software install, no sign-up required. Guests are tracked as meeting participants but never use a portal seat licence.' },
  { q: 'Is it WebRTC under the hood?', a: 'Yes. The whole meetings portal is brandable WebRTC — your users open a tab in Chrome, Edge or Safari, grant mic/camera access once, and that\'s it.' },
  { q: 'Are meetings recorded and transcribed?', a: 'Optional cloud recording is available with retention rules. Recordings are stored in the platform and replay in the portal; AI transcription + sentiment analysis can be turned on per recording or per room.' },
  { q: 'Does it integrate with Outlook / Google Calendar?', a: 'Yes. Meetings sync bi-directionally with Outlook and Google calendars — schedule from either, the join link follows.' }
];

export default function VideoConferencing() {
  return (
    <>
      <Seo
        title="HD Video Conferencing & Webinars | WebRTC | VoIPinger"
        description="Native WebRTC video meetings and webinars — up to 50 in a conference, 1,000 in a webinar. No software install, recording + transcription, Outlook + Google calendar sync."
        path="/video-conferencing"
        image="https://voipinger.com/assets/heroes/video-conferencing.png"
        service={{ name: 'Video Conferencing & Webinars', serviceType: 'Video collaboration / web conferencing' }}
        faq={FAQ}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Video Conferencing', path: '/video-conferencing' }]}
      />
      <PageHero
        eyebrow="Video Conferencing"
        title="HD video meetings, ridiculously easy"
        subtitle="Run team standups, all-hands and customer meetings without third-party tools. Same login, same dashboard, same calling experience — just better video."
        breadcrumb="Video Conferencing"
      />

      <section className="bg-white">
        <div className="container-x section-pad grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="eyebrow">Built for daily use</span>
            <h2 className="section-title mt-4">Video that respects your time</h2>
            <p className="section-sub mt-4">
              No “waiting for the host”. No app installs for guests. No mystery audio lag. Just open, click, talk — and get back to work.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2">
              {['One-click join from any browser', 'Up to 1080p video', 'Crystal-clear Opus HD audio', 'Background blur & filters', 'Screen sharing & remote control', 'AI noise suppression'].map((b) => (
                <li key={b} className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-violet-600" /><span className="text-sm">{b}</span></li>
              ))}
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="btn-primary">Start a free trial <ArrowRight size={16} /></Link>
              <Link to="/web-phone" className="btn-light">See the desktop app</Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-5 rounded-3xl bg-gradient-to-br from-violet-100 via-purple-100 to-fuchsia-100 -z-10" />
            <video
              src="/assets/videos/voipinger-voice-video.mp4"
              poster={NEW_PAGE_IMAGES.videoConf}
              className="rounded-2xl shadow-xl w-full"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-label="Video conferencing"
            />
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">Capabilities</span>
            <h2 className="section-title mt-4">Everything modern teams expect</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {VIDEO_FEATURES.map((f) => {
              const Ic = Icons[f.icon] || Icons.Sparkles;
              return (
                <div key={f.t} className="card-modern">
                  <div className="w-11 h-11 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center mb-3"><Ic size={20} /></div>
                  <h3 className="font-bold text-slate-900 mb-1">{f.t}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* SESSION TYPES — Conference / Room / Webinar                      */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">Three session types</span>
            <h2 className="section-title mt-4">One platform. The right session for the right meeting.</h2>
            <p className="section-sub mt-3">
              All three sit on the same brandable WebRTC portal — no installs for hosts or guests. Pick
              whichever matches the scenario.
            </p>
            <div className="divider-svg mt-6" />
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-violet-300 hover:shadow-md hover:-translate-y-1 transition-all">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/20">
                <Video size={22} />
              </div>
              <h3 className="font-extrabold text-slate-900 text-lg mb-2">Meeting</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">
                One-off scheduled session with a unique link. Send a calendar invite, everyone joins,
                you wrap up.
              </p>
              <p className="text-[11px] uppercase tracking-wider text-violet-600 font-semibold">Best for</p>
              <p className="text-xs text-slate-500 italic">Customer calls, ad-hoc team huddles, demos.</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-violet-300 hover:shadow-md hover:-translate-y-1 transition-all">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 text-white flex items-center justify-center mb-4 shadow-md shadow-fuchsia-500/20">
                <Users size={22} />
              </div>
              <h3 className="font-extrabold text-slate-900 text-lg mb-2">Conference Room</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">
                Persistent meeting room with its own URL — share once, reuse forever. Up to 25 full-video
                + 25 audio-only participants.
              </p>
              <p className="text-[11px] uppercase tracking-wider text-violet-600 font-semibold">Best for</p>
              <p className="text-xs text-slate-500 italic">Weekly stand-ups, standing 1:1s, drop-in rooms.</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-violet-300 hover:shadow-md hover:-translate-y-1 transition-all">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center mb-4 shadow-md shadow-amber-500/20">
                <Megaphone size={22} />
              </div>
              <h3 className="font-extrabold text-slate-900 text-lg mb-2">Webinar</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">
                Up to <strong className="text-slate-900">1,000 participants</strong> with host / attendee /
                guest roles. Speaker spotlight, chat moderation, recording.
              </p>
              <p className="text-[11px] uppercase tracking-wider text-violet-600 font-semibold">Best for</p>
              <p className="text-xs text-slate-500 italic">Product launches, training, town halls, AMAs.</p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto mt-10 rounded-2xl border border-violet-100 bg-violet-50/50 p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/30">
                <Sparkles size={22} />
              </div>
              <div>
                <h3 className="font-extrabold text-slate-900 text-lg mb-2">Participants are never capped</h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Grow as your demand grows without adding any up-front cost — guests don't burn portal
                  seat licences, so a 950-person webinar costs the same per host as a 5-person huddle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* HOST / ATTENDEE / GUEST                                          */}
      {/* ============================================================== */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">Participant roles</span>
            <h2 className="section-title mt-4">Host / Attendee / Guest — and what each can do</h2>
            <p className="section-sub mt-3">
              Three roles, clearly scoped. Lets you run a 1,000-person webinar without a single
              accidental "you're on mute" moment.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <div className="w-11 h-11 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center mb-4">
                <UserCog size={20} />
              </div>
              <h3 className="font-extrabold text-slate-900 mb-2">Host</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Any registered VoIPinger user who schedules a collaboration session, conference room
                or webinar. Full control — mute, remove, record, lock, end.
              </p>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <div className="w-11 h-11 rounded-xl bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center mb-4">
                <Users size={20} />
              </div>
              <h3 className="font-extrabold text-slate-900 mb-2">Attendee</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                A registered domain user who joined — colleagues from your tenant. Speak, share, chat,
                react. Counts towards your platform session quota.
              </p>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4">
                <Users size={20} />
              </div>
              <h3 className="font-extrabold text-slate-900 mb-2">Guest</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                External participant — no portal account. Joins from a link. Speak / share / chat at
                host discretion. <strong>Guests don't use a session licence.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* IN-MEETING CAPABILITIES                                          */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">In-meeting capabilities</span>
            <h2 className="section-title mt-4">Everything you need, nothing you don't</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              { i: ScreenShare, t: 'Screen share', d: 'Window, tab or full desktop — with active-speaker detection.' },
              { i: Mic, t: 'Active speaker', d: 'Layout highlights whoever is currently talking, automatically.' },
              { i: FileVideo, t: 'Cloud recording', d: 'Recorded to the platform with retention rules + AI transcription option.' },
              { i: Calendar, t: 'Calendar sync', d: 'Outlook + Google two-way — schedule from either side, join link follows.' }
            ].map((c) => (
              <div key={c.t} className="rounded-2xl bg-white border border-slate-200 p-5 hover:border-violet-300 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center mb-3">
                  <c.i size={18} />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">{c.t}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FaqAccordion items={FAQ} title="Video Conferencing FAQ" />
      <CtaBanner title="Cut your meeting tools bill" subtitle="Replace expensive video apps with a meeting suite that's already in your phone system." />
    </>
  );
}
